<script setup>
import { onMounted, ref } from 'vue'
import { getDefaultLocale } from '@/pages/global/utils/locale'
import Navbar from './components/Navbar.vue'
import PDFContent from '@/pages/global/pdfPreview/components/PDFContent.vue'
import axios from 'axios'
import sensor from '@utils/saUtilsGlobal'
import i18nLang from './lang.json'
// import { MockOSSData } from '@/pages/global/pdfPreview/mockData'

// 环境判断对齐 utils/global/fetchGlobalAppApi.js:vue.config.js 在 BUILD_ENV=staging
// 时把 NODE_ENV 改写为 'staging',所以 staging / prod 都通过 NODE_ENV 区分
const isEnvProduction = process.env.NODE_ENV === 'production'
const isEnvStaging = process.env.NODE_ENV === 'staging'

// 获取当前语言
const defaultLocal = getDefaultLocale()

// 检测是否为阿拉伯语（RTL语言）
const isRTL = ref(defaultLocal === 'ar')
// const isRTL = ref(true)

// i18n:同 attendanceManagement / aiTaskProcessing 等工程的 Proxy 模式,
// 模板里直接 {{ lang.i_xxx }} 取当前语种文案,fallback 到 en
const lang = new Proxy(i18nLang, {
  get(target, property) {
    if (target[property]) {
      return target[property][defaultLocal] || target[property].en
    }
    return ''
  }
})

// Photo Link 改造(V10.0.120):
//   URL filename 可能是两种前缀:
//   - "share-link/<uuid>.json"  → 新 Photo Link,server 校验 + 直接当完整 OSS path 用
//   - "<uuid>.json"             → 老 PDF Link,拼 pdf/ 前缀拿 OSS(行为零回归)
const OSS_HOST = 'https://global-cloud.dayscamera.com'
const PHOTO_LINK_PREFIX = 'share-link/'
const API_HOST = isEnvProduction
  ? 'https://app.dayscamera.com'
  : isEnvStaging
  ? 'https://app-staging.dayscamera.com'
  : 'https://app-test.dayscamera.com'

const url = new URL(window.location.href)
const filename = url.searchParams.get('filename')
const isPhotoLink = !!filename && filename.startsWith(PHOTO_LINK_PREFIX)

// 无效标志:server check 失败 / OSS 拉不到 / filename 缺失,统一切到失效页 UI(Navbar 隐藏下载按钮 + 居中文案,见 template invalid-wrapper)
const linkInvalid = ref(false)

onMounted(() => {
  init()
})

const pdfData = ref([])
const loadTime = ref()

const outerAxios = axios.create({
  timeout: 30000
})

/**
 * 初始化获取 OSS manifest 数据
 *   - Photo Link(share-link/ 前缀):先调 server checkLinkStatus 校验过期,通过后再拉 OSS
 *   - 老 PDF Link(无前缀):跟改造前行为完全一致,直接拉 OSS
 *   - 任何一步失败(filename 缺失 / valid=false / 网络异常 / OSS 拉不到):linkInvalid=true,展示空白
 */
const init = async () => {
  if (!filename) {
    linkInvalid.value = true
    handleTrack({ loadResult: 'failed', loadFailMsg: 'missing_filename' })
    return
  }

  // Photo Link 流程:先调 checkLinkStatus(server review v3 决议,filename 必须含 share-link/ prefix)
  if (isPhotoLink) {
    const valid = await checkPhotoLinkStatus()
    if (!valid) {
      linkInvalid.value = true
      return
    }
  }

  // 拉 OSS manifest
  const startTime = performance.now()
  //  Photo Link:filename 已含完整 path(share-link/xxx.json),直接 host + filename
  //  老 PDF Link:filename 不含 prefix,拼 pdf/ 前缀(跟改造前行为一致)
  const manifestUrl = isPhotoLink
    ? `${OSS_HOST}/${filename}`
    : `${OSS_HOST}/pdf/${filename}`
  try {
    pdfData.value = await outerAxios.get(manifestUrl).then((res) => {
      if (res.status === 200) {
        return res.data
      }
    })
    loadTime.value = performance.now() - startTime
    handleTrack()
  } catch (error) {
    linkInvalid.value = true
    handleTrack({
      loadResult: 'failed',
      loadFailMsg: JSON.stringify(error)
    })
  }
}

/**
 * 调 server `/next/h5/share/checkLinkStatus` 校验 Photo Link 是否有效
 *
 * server 接口契约(tech-server.md §4.2 / tech-web.md §2.2):
 *   GET <API_HOST>/next/h5/share/checkLinkStatus?ossFilename=share-link/<uuid>.json
 *   200 { code: 200, data: { valid: bool, expireAt: number(ms) } }
 *   server 严格匹配,ossFilename **必须含 share-link/ prefix**(无 strip / 无 normalize)
 *
 * @returns valid=true 时返 true,其余(valid=false / 网络异常 / 业务码非 200)都返 false
 */
const checkPhotoLinkStatus = async () => {
  try {
    const res = await outerAxios.get(
      `${API_HOST}/next/h5/share/checkLinkStatus`,
      { params: { ossFilename: filename } }
    )
    if (res.data?.code !== 200) {
      handleTrack({
        loadResult: 'failed',
        loadFailMsg: `checkLinkStatus_code_${res.data?.code}`
      })
      return false
    }
    const valid = !!res.data?.data?.valid
    if (!valid) {
      handleTrack({
        loadResult: 'failed',
        loadFailMsg: 'link_invalid'
      })
    }
    return valid
  } catch (error) {
    handleTrack({
      loadResult: 'failed',
      loadFailMsg: 'checkLinkStatus_err: ' + JSON.stringify(error)
    })
    return false
  }
}

/**
 * 上报埋点事件
 * */
const handleTrack = (params) => {
  sensor.track('H5_enter_page_pdf_link', {
    loadResult: 'succ',
    url: pdfData.value.pdfUrl,
    linkType: isPhotoLink ? 'photo_link' : 'pdf_link',
    ...params
  })
}
</script>

<template>
  <div class="container-wrapper" :dir="isRTL ? 'rtl' : 'ltr'">
    <!-- 失效页:Navbar(隐藏下载按钮) + 居中两行文案 -->
    <template v-if="linkInvalid">
      <Navbar
        :pdfUrl="''"
        :downloadName="''"
        :isRTL="isRTL"
        :hideDownload="true"
      />
      <div class="invalid-wrapper">
        <p class="invalid-title">{{ lang.i_this_link_has_expired }}</p>
        <p class="invalid-desc">
          {{
            lang.i_to_keep_work_photos_secure_shared_links_automatically_expi
          }}
        </p>
      </div>
    </template>

    <!-- 正常:Navbar + PDFContent -->
    <template v-else>
      <Navbar
        :pdfUrl="pdfData.pdfUrl"
        :downloadName="`${pdfData.header?.title}_${pdfData.header?.createDate}${
          pdfData.header?.author ? `_${pdfData.header?.author}` : ''
        }.pdf`"
        :isRTL="isRTL"
      />
      <div class="content-wrapper">
        <PDFContent :dataSource="pdfData" :loadTime="loadTime" :isRTL="isRTL" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.container-wrapper {
  min-height: 100vh;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
  padding-top: 52px;

  .content-wrapper {
    flex-grow: 1;
    min-height: calc(100vh - 85px);
    margin: 4px auto 0 auto;
  }

  /* 失效页:撑满 Navbar 之下空间,居中两行文案(对照 Figma)*/
  .invalid-wrapper {
    flex-grow: 1;
    min-height: calc(100vh - 52px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    text-align: center;
    box-sizing: border-box;

    .invalid-title {
      font-size: 16px;
      font-weight: 600;
      color: #000;
      line-height: 1.4;
      margin-bottom: 12px;
    }

    .invalid-desc {
      font-size: 14px;
      font-weight: 400;
      color: #000;
      line-height: 1.5;
      max-width: 480px;
    }
  }
}
</style>

<style>
* {
  padding: 0;
  margin: 0;
}

html,
body {
  background: #f0f0f0;
  overscroll-behavior: none;
  font-family: 'SF Pro', 'Myriad Set Pro', 'SF Pro Icons',
    'Apple Legacy Chevron', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
}

.div {
  height: 50px;
  background: #ccc;
  margin-bottom: 10px;
}

/* 作用于滚动容器 */
.scroll-container {
  overflow: auto;
  scrollbar-width: thin; /* Firefox 设置细滚动条 */
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* Firefox 配色 */
}

/* WebKit 浏览器下滚动条样式（Chrome、Edge、Safari） */
.scroll-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}
</style>
